const styles = [
  {
    id: 'scalping',
    title: 'Scalping',
    tagline: 'Volume amplifies every fraction of a pip',
    icon: 'ri-flashlight-fill',
    color: '#2563EB',
    accentLight: '#EEF4FF',
    accentBorder: '#BED9FF',
    profile: '10–30 trades/day · seconds to minutes · tight 3–8 pip targets',
    coreProblem: 'At 20 trades per day, a 0.3 pip spread difference = $60/day difference. Over a year, that\'s $15,000 — from what looks like a tiny spread gap.',
    costRows: [
      { label: 'Spread', range: '0.3 – 1.0 pips', impact: 'Critical — primary cost driver', note: '15 RT/day at 0.6p = $90/day' },
      { label: 'Commission', range: '$4 – $7 / RT lot', impact: 'Can exceed spread on ECN', note: '20 RT/day at $7 = $140/day' },
      { label: 'Slippage', range: 'Rare (limit orders)', impact: 'Low — scalpers use limits', note: 'Market orders avoided' },
      { label: 'Swap', range: 'None', impact: 'Positions closed before rollover', note: 'Not a factor' },
    ],
    monthlyEstimate: '~$2,860',
    estimateLabel: 'Estimated monthly',
    estimateDetail: '20 trades/day, 0.6p spread, $3.50/side commission, 22 trading days',
    takeaway: 'ECN with 0.2p + $7 commission often beats "commission-free" with 1.0p spread. Do the math.',
  },
  {
    id: 'day',
    title: 'Day Trading',
    tagline: 'Timing matters more than tight spreads',
    icon: 'ri-sun-fill',
    color: '#00A6B4',
    accentLight: '#E6FAFC',
    accentBorder: '#B8E8EC',
    profile: '1–5 trades/day · minutes to hours · no overnight positions',
    coreProblem: 'Spread widening during news can turn a planned 0.8 pip entry into 3+ pips. One bad timing decision can cost more than a week of normal spreads.',
    costRows: [
      { label: 'Spread', range: '0.5 – 1.5 pips', impact: 'Widens during news events', note: '3 trades at 1.0p = $30/day' },
      { label: 'Commission', range: '$4 – $7 / RT lot', impact: 'Less impactful than scalpers', note: 'Lower volume = lower total' },
      { label: 'Slippage', range: 'Moderate to high', impact: 'Market orders during NFP/CPI', note: '2p slip on 1 trade = $20' },
      { label: 'Swap', range: 'None', impact: 'All positions closed intraday', note: 'Not a factor' },
    ],
    monthlyEstimate: '~$990',
    estimateLabel: 'Estimated monthly',
    estimateDetail: '3 trades/day, 1.0p spread, $5/RT commission, 22 trading days',
    takeaway: 'Trade during liquid sessions. Avoid market orders during high-impact news. The spread on screen may not be your fill price.',
  },
  {
    id: 'swing',
    title: 'Swing Trading',
    tagline: 'Holding time is the hidden cost multiplier',
    icon: 'ri-moon-fill',
    color: '#F59E0B',
    accentLight: '#FFF7E6',
    accentBorder: '#F5D89C',
    profile: '2–10 positions/month · days to weeks · wider profit targets',
    coreProblem: 'Swap accumulates silently. A 10-day hold at 0.8 pips/day swap = 8 pips — often more than the spread. On some pairs, swap alone can flip a winning trade negative.',
    costRows: [
      { label: 'Spread', range: '0.6 – 2.0 pips', impact: 'One-time cost, less critical', note: 'Paid once per position' },
      { label: 'Commission', range: '$4 – $7 / RT lot', impact: 'Small relative to holding', note: 'Low volume = low total' },
      { label: 'Slippage', range: 'Low to moderate', impact: 'Limit orders sufficient', note: 'Not time-sensitive entries' },
      { label: 'Swap', range: '0.2 – 1.5 pips/day', impact: 'Accumulates — largest cost', note: '10d at 0.8p = 8 pips swap' },
    ],
    monthlyEstimate: '~$495',
    estimateLabel: 'Estimated monthly',
    estimateDetail: '3 positions/month, 10-day hold, 0.8p/day swap, GBP/JPY ¥960/pip',
    takeaway: 'Always check swap rates before holding overnight. On certain pairs, a swap-free account can reduce costs by ~80%.',
  },
];

export default function TradingStyleSection() {
  return (
    <section className="w-full bg-background-100 overflow-hidden">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-12 md:py-[88px]">
        {/* Asymmetric header */}
        <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-12 mb-10 md:mb-16">
          <div className="flex-1">
            <h2 className="text-[24px] md:text-[38px] font-[750] leading-[30px] md:leading-[46px] text-[#101828] tracking-[-0.015em] max-w-[620px]">
              Your style picks which cost dominates.
            </h2>
          </div>
          <p className="text-[13px] md:text-[14px] leading-[20px] md:leading-[22px] text-[#667085] max-w-[240px] flex-shrink-0 md:text-right">
            Spread, commission, or swap — one always hits hardest.
          </p>
        </div>

        {/* Three full-width horizontal panels */}
        <div className="flex flex-col gap-4 md:gap-6">
          {styles.map((style) => (
            <div
              key={style.id}
              className="group flex flex-col lg:flex-row rounded-2xl md:rounded-3xl overflow-hidden border transition-all duration-300 hover:border-[#D1D8E0]"
              style={{ backgroundColor: '#FFFFFF', borderColor: '#E6EAF0' }}
            >
              {/* Left accent sidebar */}
              <div
                className="flex-shrink-0 flex lg:flex-col items-center gap-3 lg:gap-5 px-4 py-3 md:p-5 lg:p-0 lg:w-[100px] lg:justify-center lg:pt-8 lg:pb-8"
                style={{ backgroundColor: style.accentLight }}
              >
                <div
                  className="w-9 h-9 lg:w-12 lg:h-12 rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${style.color}16` }}
                >
                  <i className={`${style.icon} text-base lg:text-xl`} style={{ color: style.color }}></i>
                </div>
                <div className="lg:text-center lg:max-w-[80px]">
                  <h3 className="text-[15px] md:text-[17px] lg:text-[18px] font-[750] text-[#101828] leading-tight">{style.title}</h3>
                  <p className="hidden lg:block text-[10px] font-semibold uppercase tracking-[0.05em] mt-1.5 leading-[14px]" style={{ color: style.color }}>{style.tagline}</p>
                </div>
              </div>

              {/* Main content */}
              <div className="flex-1 p-4 md:p-6 lg:p-7 flex flex-col lg:flex-row gap-4 md:gap-5 lg:gap-7">
                {/* Left: explanation */}
                <div className="flex-1 lg:max-w-[40%] flex flex-col gap-3 md:gap-4">
                  <p className="text-[11px] md:text-[12px] text-[#667085] font-medium lg:hidden" style={{ color: style.color }}>{style.tagline}</p>
                  <span className="inline-flex self-start text-[10px] md:text-[11px] font-semibold text-[#94A3B8] bg-[#F6F8FB] px-2 py-0.5 md:px-2.5 md:py-1 rounded-full">
                    {style.profile}
                  </span>
                  <p className="text-[13px] md:text-[15px] leading-[22px] md:leading-[26px] text-[#475467]">
                    {style.coreProblem}
                  </p>

                  {/* Monthly cost estimate */}
                  <div className="flex items-center gap-3 mt-auto">
                    <span className="text-[24px] md:text-[30px] font-[800] tracking-[-0.02em]" style={{ color: style.color }}>{style.monthlyEstimate}</span>
                    <div className="flex flex-col">
                      <span className="text-[10px] md:text-[11px] font-semibold text-[#101828] leading-tight">{style.estimateLabel}</span>
                      <span className="text-[10px] text-[#94A3B8] leading-[14px] md:leading-[15px]">{style.estimateDetail}</span>
                    </div>
                  </div>
                </div>

                {/* Right: cost table */}
                <div className="flex-1 lg:max-w-[60%] flex flex-col gap-3">
                  {/* ── Desktop table (md+) ── */}
                  <div className="hidden md:flex flex-col gap-px rounded-xl overflow-hidden" style={{ backgroundColor: '#E6EAF0' }}>
                    {/* Header */}
                    <div className="grid grid-cols-[0.7fr_1fr_0.9fr_0.8fr] gap-2 px-3 py-2.5" style={{ backgroundColor: style.accentLight }}>
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#94A3B8]">Cost Type</span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#94A3B8]">Typical Range</span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#94A3B8]">Impact</span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#94A3B8]">Real Example</span>
                    </div>
                    {/* Rows */}
                    {style.costRows.map((row) => (
                      <div key={row.label} className="grid grid-cols-[0.7fr_1fr_0.9fr_0.8fr] gap-2 px-3 py-2.5 bg-white hover:bg-[#FAFBFC] transition-colors">
                        <span className="text-[12.5px] font-semibold text-[#101828]">{row.label}</span>
                        <span className="text-[12.5px] font-[700]" style={{ color: style.color }}>{row.range}</span>
                        <span className="text-[12px] text-[#475467] leading-[17px]">{row.impact}</span>
                        <span className="text-[11px] text-[#94A3B8] leading-[16px]">{row.note}</span>
                      </div>
                    ))}
                  </div>

                  {/* ── Mobile table (below md) ── */}
                  <div className="md:hidden flex flex-col gap-px rounded-lg overflow-hidden" style={{ backgroundColor: '#E6EAF0' }}>
                    {/* Header */}
                    <div className="grid grid-cols-[1fr_1.2fr] gap-2 px-3 py-2" style={{ backgroundColor: style.accentLight }}>
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#94A3B8]">Cost Type</span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#94A3B8]">Range &amp; Impact</span>
                    </div>
                    {/* Rows — each row is a 2-col layout with impact + note underneath */}
                    {style.costRows.map((row) => (
                      <div key={row.label} className="bg-white hover:bg-[#FAFBFC] transition-colors px-3 py-2.5">
                        <div className="grid grid-cols-[1fr_1.2fr] gap-2 mb-1.5">
                          <span className="text-[12px] font-semibold text-[#101828]">{row.label}</span>
                          <span className="text-[12px] font-[700]" style={{ color: style.color }}>{row.range}</span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[11px] text-[#475467] leading-[16px]">{row.impact}</span>
                          <span className="text-[10.5px] text-[#94A3B8] leading-[15px]">{row.note}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Takeaway */}
                  <div className="flex items-start gap-2">
                    <i className="ri-lightbulb-flash-line text-[12px] md:text-[13px] mt-px flex-shrink-0" style={{ color: style.color }}></i>
                    <p className="text-[11px] md:text-[12px] leading-[17px] md:leading-[18px] text-[#667085]">
                      <strong className="text-[#101828] font-semibold">Key takeaway: </strong>
                      {style.takeaway}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-6 md:mt-8 text-center text-[11px] md:text-[12px] leading-[18px] md:leading-[20px] text-[#94A3B8] max-w-[600px] mx-auto">
          Estimates based on typical retail trading conditions and 1 standard lot size. Actual costs vary by broker, pair, and market conditions.
        </p>
      </div>
    </section>
  );
}